import React, { createContext, useContext, useMemo } from 'react';
import {
  buildLatestChangeMap,
  formatDiffValue,
  getChangeForField,
} from '../../../utils/vehicleDiff';

const VehicleDiffContext = createContext({ map: new Map(), currency: 'MAD', idLabels: {} });

export const VehicleDiffProvider = ({ history, lastChanges, currency = 'MAD', idLabels = {}, children }) => {
  const map = useMemo(
    () => buildLatestChangeMap(history, lastChanges, idLabels),
    [history, lastChanges, idLabels]
  );
  const value = useMemo(() => ({ map, currency, idLabels }), [map, currency, idLabels]);
  return <VehicleDiffContext.Provider value={value}>{children}</VehicleDiffContext.Provider>;
};

export const useVehicleDiffMap = () => useContext(VehicleDiffContext);

export const useFieldChange = (fieldKey) => {
  const { map, currency, idLabels } = useContext(VehicleDiffContext);
  return { change: getChangeForField(map, fieldKey), currency, idLabels };
};

/**
 * Enveloppe un champ de fiche véhicule : rendu classique si inchangé,
 * alerte + ancienne (barrée) / nouvelle (verte) valeur si présent dans l’historique.
 *
 * @example
 * <DiffField fieldKey="pricing.pricePerDay" label="Prix journalier">
 *   {vehicle.pricing?.basePrice} MAD
 * </DiffField>
 */
const DiffField = ({
  fieldKey,
  label,
  children,
  formatValue,
  className = '',
}) => {
  const { change, currency, idLabels } = useFieldChange(fieldKey);
  const fmt = formatValue || ((value) => formatDiffValue(value, { currency, idLabels }));
  const oldLabel = change ? fmt(change.oldValue) : '';
  const newLabel = change ? fmt(change.newValue) : '';
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const isBlank = (value) => {
    const normalized = normalize(value);
    return !normalized || normalized === '—' || normalized === '-' || normalized === 'non renseigné';
  };
  const TYPE_DEFAULTS = new Set(['car', 'van', 'moto', 'motorcycle', 'bike', 'truck', 'bus', 'suv', 'vehicle']);
  const isTypeField =
    fieldKey === 'type' ||
    fieldKey === 'vehicleType' ||
    fieldKey === 'item_type';
  const isDefaultTypeEcho = TYPE_DEFAULTS.has(normalize(newLabel));
  const isChanged =
    Boolean(change) &&
    normalize(oldLabel) !== normalize(newLabel) &&
    !(isBlank(oldLabel) && isBlank(newLabel)) &&
    !isBlank(newLabel) &&
    !(isBlank(oldLabel) && isDefaultTypeEcho) &&
    !(isTypeField && isBlank(oldLabel)) &&
    !(isTypeField && isDefaultTypeEcho);

  const wrapClass = isChanged
    ? `rounded-lg border border-red-200 bg-red-50 p-2 ${className}`.trim()
    : className;

  return (
    <div className={wrapClass || undefined}>
      {label != null && label !== '' && (
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm text-gray-500">{label}</span>
          {isChanged && (
            <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-500 text-white rounded">
              Modifié
            </span>
          )}
        </div>
      )}
      <div className={isChanged && (label == null || label === '') ? 'relative' : undefined}>
        {isChanged && (label == null || label === '') && (
          <span className="absolute top-1 right-1 z-10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-500 text-white rounded">
            Modifié
          </span>
        )}
        {children}
      </div>
      {isChanged && (
        <div className="mt-1.5 text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="line-through text-red-600">{oldLabel}</span>
          <span className="text-gray-400" aria-hidden>
            →
          </span>
          <span className="text-green-600 font-bold">{newLabel}</span>
        </div>
      )}
    </div>
  );
};

export default DiffField;
